<template>
  <teleport to="body">
    <div v-if="isOpen" class="splash-container">
      <v-card class="splash-card" elevation="8">
        <div class="d-flex pa-3 gap-3">
          <img src="@/assets/img/edgefirst_hexagon.svg" alt="EdgeFirst Studio" width="100" height="100">
          <div class="d-flex flex-column gap-3 flex-grow-1">
            <div class="d-flex justify-end">
              <v-btn icon size="small" @click="handleClose">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
            <p class="text-body1 mb-0 px-3 py-2">
              Sign in to access profiling results, datasets and models for free.
            </p>
          </div>
        </div>

        <v-card-text class="text-center pt-0 pb-6">
          <div class="space-y-3">
            <v-btn
              block
              color="primary"
              size="large"
              class="mb-3"
              @click="navigateToLogin"
            >
              Sign In
            </v-btn>

            <v-btn
              block
              variant="outlined"
              color="primary"
              size="large"
              @click="navigateToSignup"
            >
              Create Account
            </v-btn>
          </div>
        </v-card-text>

        <v-card-actions class="justify-center pb-4">
          <span class="text-caption text-disabled">
            You can explore public content or sign in to continue.
          </span>
        </v-card-actions>
      </v-card>
    </div>
  </teleport>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { safeSessionStorage } from '@/utils/safeStorage';

const router = useRouter();
const route = useRoute();
const store = useStore();

const isOpen = ref(false);
const returnPath = ref(null);
let reopenTimeout = null;

const hasToken = computed(() => !!store.state.user.token);

// Close and reset when route changes
watch(() => route.path, () => {
  isOpen.value = false;
  if (reopenTimeout) {
    clearTimeout(reopenTimeout);
    reopenTimeout = null;
  }
});

onMounted(() => {
  // Store the current path to redirect back after login
  returnPath.value = route.path;
  // Show splash screen after 5 seconds
  reopenTimeout = setTimeout(() => {
    checkAndShow();
  }, 5000);
});

onUnmounted(() => {
  if (reopenTimeout) {
    clearTimeout(reopenTimeout);
  }
});

function checkAndShow() {
  if (!hasToken.value) {
    isOpen.value = true;
  }
}

function handleClose() {
  isOpen.value = false;
  // Re-show after 5 seconds
  if (reopenTimeout) {
    clearTimeout(reopenTimeout);
  }
  reopenTimeout = setTimeout(() => {
    checkAndShow();
  }, 5000);
}

function navigateToLogin() {
  // Store the return path in session storage so it survives OAuth redirects
  console.log('[RegisterSplashScreen] navigateToLogin called, returnPath:', returnPath.value);
  if (returnPath.value && returnPath.value !== '/login' && returnPath.value !== '/signup') {
    safeSessionStorage.setItem('login_return_path', returnPath.value);
    console.log('[RegisterSplashScreen] Stored login_return_path:', returnPath.value);
  } else {
    console.log('[RegisterSplashScreen] Skipped storing path (likely already on login/signup page)');
  }
  router.push({
    path: '/login',
    query: { redirectPath: returnPath.value }
  });
}

function navigateToSignup() {
  // Store the return path in session storage so it survives OAuth redirects
  if (returnPath.value && returnPath.value !== '/login' && returnPath.value !== '/signup') {
    safeSessionStorage.setItem('login_return_path', returnPath.value);
  }
  router.push({
    path: '/signup',
    query: { redirectPath: returnPath.value }
  });
}
</script>

<style lang="scss" scoped>
.space-y-3 {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.splash-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  pointer-events: none;
}

.splash-card {
  max-width: 500px;
  width: 500px;
  pointer-events: all;
}
</style>
